import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../store/store';
import { API_CONFIG } from '../config/api.config';

export interface SiteData {
  siteId: number;
  siteDescription: string;
  imageBuffer?: string;
  scale: number;
  sensorPositions: Array<{
    sensorId: string;
    x: number;
    y: number;
  }>;
  siteUsers?: string[];
  siteImage?: string;
}

interface SitePlanData {
  siteId: number;
  siteDescription: string;
  imageBuffer: string;
  scale: number;
  sensorPositions: Array<{
    sensorId: number;
    x: number;
    y: number;
  }>;
  siteUsers: number[];
  siteImage: string;
}

interface SitePlanResponse {
  siteId: number;
  siteDescription: string;
  siteplan: string;
  scale: number;
  positions: string; // JSON string of sensor positions
  siteUsers: string; // JSON string of user IDs
  siteImage: string;
}

interface GetSitesResponse {
  status: string;
  data: SitePlanResponse[];
}

export const siteApi = createApi({
  reducerPath: 'siteApi',
  baseQuery: fetchBaseQuery({ 
    baseUrl: API_CONFIG.baseURL,
    prepareHeaders: (headers, { getState }) => {
      const user = (getState() as RootState).auth.user;
      
      headers.set('Content-Type', 'application/json');
      headers.set('x-company-id', user?.companyID?.toString() || '');
      headers.set('x-company-name', user?.companyName || '');
      
      return headers;
    },
  }),
  tagTypes: ['Site'],
  endpoints: (builder) => ({
    getSites: builder.query<GetSitesResponse, void>({
      query: () => '/sites/',
      transformResponse: (response: GetSitesResponse) => response,
    }),
    getSiteById: builder.query<SiteData, number>({
      query: (id) => `sites/${id}`,
      providesTags: (_result, _err, id) => [{ type: 'Site', id }],
    }),
    createSite: builder.mutation<SiteData, Partial<SiteData>>({
      query: (site) => ({
        url: 'sites/',
        method: 'POST',
        body: site,
      }),
      invalidatesTags: ['Site'],
    }),
    updateSite: builder.mutation<SiteData, Partial<SiteData> & { siteId: number }>({
      query: ({ siteId, ...site }) => ({
        url: `sites/${siteId}`,
        method: 'PUT',
        body: site,
      }),
      invalidatesTags: (_result, _err, { siteId }) => [{ type: 'Site', siteId }],
    }),
    uploadSitePlan: builder.mutation<any, SitePlanData>({
      query: (data) => ({
        url: '/sites',
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

export const {
  useGetSitesQuery,
  useGetSiteByIdQuery,
  useCreateSiteMutation,
  useUpdateSiteMutation,
  useUploadSitePlanMutation,
} = siteApi; 